import React from 'react';
import { Link } from 'react-router-dom';
import { NavBar, Footer, Placeholder } from '../../components/redesign/shared.jsx';
import SEO from '../../components/SEO.jsx';
import { useCurrency } from '../../utils/currency.js';

export function HomeA({ onNav }) {
  const { price } = useCurrency();
  return (
    <div className="pageA">
      <SEO
        title="Aakeeo — Open-source CRM consultancy | SuiteCRM implementation"
        description="Custom CRMs on SuiteCRM and open foundations. For teams above $1M revenue who want data ownership, no per-seat fees, and a system shaped to their process."
        keywords={['open source CRM', 'SuiteCRM consultancy', 'CRM implementation', 'self-hosted CRM', 'enterprise CRM']}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Aakeeo Technologies',
          url: 'https://www.aakeeo.com',
          logo: 'https://www.aakeeo.com/logo.png',
          foundingDate: '2014',
          description: 'Open-source CRM consultancy specialising in SuiteCRM implementation, customisation, integration and AI workflow automation.',
          sameAs: [
            'https://www.linkedin.com/company/aakeeo-technologies-pvt-ltd',
            'https://github.com/Aakeeo',
            'https://x.com/AakeeoTech',
          ],
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+91-8431748754',
            email: 'sales@aakeeo.com',
            contactType: 'sales',
            areaServed: ['IN', 'UK', 'US', 'AU', 'FR'],
            availableLanguage: 'English',
          },
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Bengaluru',
            addressCountry: 'IN',
          },
        }}
      />
      <NavBar page="home" onNav={onNav} />

      <main>

      <section className="heroA">
        <div className="heroA-meta">
          <span className="heroA-kicker">Open-source CRM consultancy · Est. 2014</span>
        </div>
        <h1 className="heroA-title">
          Build a CRM you <em>own</em>.<br />
          Not one you keep <span className="heroA-strike">renting</span>.
        </h1>
        <p className="heroA-sub">
          We engineer custom CRMs on SuiteCRM and open foundations — for teams that&nbsp;outgrew per-seat pricing, can&apos;t ship their data offshore, and want a system shaped to their process, not a template.
        </p>
        <div className="heroA-cta">
          <button className="btnA-primary" onClick={() => onNav('contact')}>Book a strategy call</button>
          <button className="btnA-ghost" onClick={() => onNav('solutions')}>Explore our solutions →</button>
        </div>
        <div className="heroA-stats">
          <div><strong>12</strong><span>years delivering</span></div>
          <div><strong>40%</strong><span>avg. manual work removed</span></div>
          <div><strong>0</strong><span>per-seat fees, forever</span></div>
          <div><strong>100%</strong><span>code &amp; data ownership</span></div>
        </div>
        <div className="heroA-logos">
          <span className="heroA-logos-label">Trusted by teams in</span>
          <span>UK</span><span>·</span><span>Australia</span><span>·</span><span>France</span><span>·</span><span>USA</span><span>·</span><span>Nepal</span>
        </div>
      </section>

      <section className="probA">
        <div className="probA-head">
          <span className="eyebrowA">01 — The quiet CRM problem</span>
          <h2>You&apos;re paying for a CRM, or just renting someone else&apos;s vision?</h2>
        </div>
        <div className="probA-cols">
          <article className="probA-card">
            <span className="probA-tag">The promise</span>
            <p className="probA-lead">The demo was slick. The promises were big.</p>
            <p>&ldquo;This will transform your sales process.&rdquo; You signed the contract, named your admin, ran the training.</p>
          </article>
          <article className="probA-card probA-card--muted">
            <span className="probA-tag">Three months later</span>
            <ul className="probA-list">
              <li>The team is back on Excel</li>
              <li>Quick updates happen in WhatsApp</li>
              <li>Sticky notes on monitors</li>
              <li>The CRM? The system everyone avoids</li>
            </ul>
          </article>
          <article className="probA-card">
            <span className="probA-tag">The cost</span>
            <p className="probA-lead">Every new hire is another {price({ usd: '$50', inr: '₹4k' })} / month.</p>
            <p>Every feature request is &ldquo;not in your plan.&rdquo; Every integration is another plugin, another fee, another headache.</p>
          </article>
          <article className="probA-card probA-card--accent">
            <span className="probA-tag">The truth</span>
            <p className="probA-pull">
              Most CRMs were built to <em>sell subscriptions</em> — not to solve&nbsp;problems.
            </p>
          </article>
        </div>
      </section>

      <section className="pillA">
        <span className="eyebrowA">02 — What we build</span>
        <h2 className="pillA-h">
          A CRM that fits your process. Deployed on your servers. Paid for&nbsp;<em>once</em>.
        </h2>
        <div className="pillA-grid">
          <div className="pillA-card">
            <div className="pillA-num">I.</div>
            <h3>Implementation</h3>
            <p>SuiteCRM deployed, configured, hardened. Production-ready in weeks, not quarters.</p>
            <Link className="pillA-link" to="/services/suitecrm-implementation">Read more →</Link>
          </div>
          <div className="pillA-card">
            <div className="pillA-num">II.</div>
            <h3>Customisation</h3>
            <p>Modules, workflows, roles and layouts engineered around how your team actually sells.</p>
            <Link className="pillA-link" to="/services/suitecrm-customisation">Read more →</Link>
          </div>
          <div className="pillA-card">
            <div className="pillA-num">III.</div>
            <h3>Integration</h3>
            <p>ERPs, WhatsApp, payment gateways, data warehouses. The CRM becomes the spine.</p>
            <Link className="pillA-link" to="/services/crm-integration">Read more →</Link>
          </div>
          <div className="pillA-card">
            <div className="pillA-num">IV.</div>
            <h3>AI Workflow Automation</h3>
            <p>Lead scoring, smart routing and automated follow-ups that stay inside your system of record.</p>
            <Link className="pillA-link" to="/services/ai-workflow-automation">Read more →</Link>
          </div>
          <div className="pillA-card">
            <div className="pillA-num">V.</div>
            <h3>Support &amp; Maintenance</h3>
            <p>Upgrades, patches, monitoring. We stay the engineering team behind your CRM.</p>
            <Link className="pillA-link" to="/services/suitecrm-support">Read more →</Link>
          </div>
          <div className="pillA-card pillA-card--cta">
            <h3>Not sure where you sit?</h3>
            <p>Send over your current setup — we&apos;ll map the gaps in a 30-minute call.</p>
            <button className="btnA-primary btnA-primary--sm" onClick={() => onNav('contact')}>Book a call</button>
          </div>
        </div>
      </section>

      <section className="valA">
        <div className="valA-copy">
          <span className="eyebrowA">03 — All-inclusive value</span>
          <h2>
            One investment.<br />
            No subscriptions.<br />
            Infinite <em>seats</em>.
          </h2>
          <p className="valA-lead">
            The maths that flipped CRM for our clients above {price({ usd: '$1M', inr: '₹8Cr' })} revenue: a one-time build and a predictable monthly, versus a per-seat line that grows with every hire.
          </p>
        </div>
        <div className="valA-points">
          <div>
            <h3>Fixed, upfront cost</h3>
            <p>Your complete software built, tested, and deployed with every feature and integration you need — no &ldquo;surprise&rdquo; line items as we go.</p>
          </div>
          <div>
            <h3>No recurring charges</h3>
            <p>Forget monthly licences or annual renewals. Once it&apos;s live it&apos;s 100% yours — use it across however many seats you need, forever.</p>
          </div>
          <div>
            <h3>Faster ROI</h3>
            <p>With no ongoing software bills, clients typically recoup their investment in weeks through saved time and extra deals closed.</p>
          </div>
        </div>
      </section>

      <section className="procA">
        <div className="procA-head">
          <span className="eyebrowA">04 — How we work</span>
          <h2>A four-step process that gets you to production — and stays&nbsp;with&nbsp;you.</h2>
        </div>
        <ol className="procA-steps">
          <li>
            <span className="procA-num">01</span>
            <h3>Needs Discovery</h3>
            <p>Deep dive into your workflows, tools and data. We map what exists before we propose what&apos;s next.</p>
          </li>
          <li>
            <span className="procA-num">02</span>
            <h3>Architecture &amp; Scope</h3>
            <p>A fixed-price proposal with modules, integrations, timelines and a rollout plan you can actually show your board.</p>
          </li>
          <li>
            <span className="procA-num">03</span>
            <h3>Build &amp; Migrate</h3>
            <p>Engineering sprints with weekly demos. Your data migrated, roles provisioned, team trained.</p>
          </li>
          <li>
            <span className="procA-num">04</span>
            <h3>Run &amp; Evolve</h3>
            <p>Managed hosting, upgrades and new feature work on a predictable retainer — not a subscription.</p>
          </li>
        </ol>
      </section>

      <section className="testA">
        <span className="eyebrowA">05 — What clients say</span>
        <h2 className="testA-h">
          Teams on three continents, shipping on&nbsp;<em>open source</em>.
        </h2>
        <div className="testA-grid">
          <figure className="testA-small">
            <p>&ldquo;Aakeeo is an amazing team of SuiteCRM developers. Expert at what they do, they went beyond their duty and delivered so much extra. Very generous with their time and super responsive.&rdquo;</p>
            <figcaption><strong>Mustafa</strong> · Vocal Legal, UK</figcaption>
          </figure>
          <figure className="testA-small">
            <p>&ldquo;A pleasure to work with. Very professional and provided solutions to our requirements. Would recommend on any Suite/Sugar CRM project.&rdquo;</p>
            <figcaption><strong>Brendon Bell</strong> · Edu Camp, AU</figcaption>
          </figure>
          <figure className="testA-small">
            <p>&ldquo;Not only delivered a very fine quality job on time, but was also helpful, pedagogical and constructive in the design of the solution.&rdquo;</p>
            <figcaption><strong>Thomas</strong> · Fregat.io, FR</figcaption>
          </figure>
          <figure className="testA-small">
            <p>&ldquo;Working with Arun on our SuiteCRM implementation was seamless and highly productive. Deep expertise, proactive approach, and commitment to understanding our needs made the transition smooth and efficient.&rdquo;</p>
            <figcaption><strong>Benktesh Sharma</strong> · Verra.org</figcaption>
          </figure>
        </div>
      </section>

      <section className="ctaA">
        <div className="ctaA-inner">
          <h2>Save time. Cut costs. Scale&nbsp;faster.</h2>
          <p>Clients typically see a <strong>40% reduction in manual tasks</strong> and recoup their entire investment within months. Let&apos;s design the one that works for&nbsp;you.</p>
          <div className="ctaA-buttons">
            <button className="btnA-primary" onClick={() => onNav('contact')}>Contact us</button>
            <button className="btnA-ghost" onClick={() => onNav('blog')}>Read case studies →</button>
          </div>
        </div>
      </section>

      </main>

      <Footer onNav={onNav} />
    </div>
  );
}
